import { isNativeWebView } from '@/common/utils/isNativeWebView';
import { isPWA } from '@/common/utils/isPwa';
import { getCookie } from 'cookies-next';
import getConfig from 'next/config';
import { useEffect, useState } from 'react';
import { HINT_CONTROL, HINT_LEAVE_MS, HINT_ROTATE_MS, HintArm, hintArm, hintPlaceholder, nextHintStep, parseHintPercent } from '../functions/searchHint';

const { publicRuntimeConfig } = getConfig() ?? {};

/** The visitor's arm on the home page; the same hash gives the same answer on every call. `null` = outside the experiment. */
export const decideHomeHintArm = (): HintArm | null =>
  hintArm({
    terminalId: getCookie('terminal_id') as string | undefined,
    percent: parseHintPercent(publicRuntimeConfig?.SEARCH_HOME_HINT_PERCENT),
    isApp: isPWA() || isNativeWebView(),
  });

const prefersReducedMotion = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The search box hint.
 *  - not the home page ⇒ always «جستجو در پذیرش۲۴»;
 *  - home page ⇒ the visitor's arm, decided after mount (the first paint keeps today's hint, no layout jump);
 *  - `keep` ⇒ leave the page's own hint (the app home).
 * The rotation stops while the box is focused, open or has text, and stays on the first example when the visitor asks for less motion.
 */
export const useSearchHint = ({ scope, paused }: { scope: 'home' | 'keep' | 'other'; paused: boolean }): { placeholder: string | undefined; arm: HintArm | null; leaving: boolean; animated: boolean } => {
  const [arm, setArm] = useState<HintArm | null>(null);
  const [step, setStep] = useState(0);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (scope === 'home') setArm(decideHomeHintArm());
  }, [scope]);

  // The box keeps showing its hint while it is focused and empty, and the Plasmic focus callback does not fire on
  // desktop click (seen in the browser 2026-10-31). Ask the document at each tick instead of tracking focus events:
  // on mobile the focused overlay input is unmounted when it closes and no blur is sent, so a remembered flag
  // would stay "focused" for good (review 2026-10-31). The box ids are searchInput / searchInput23.
  useEffect(() => {
    if (scope !== 'home' || arm !== 'rotating' || paused || prefersReducedMotion()) return;
    let swap: ReturnType<typeof setTimeout> | undefined;
    const id = setInterval(() => {
      if (document.activeElement?.id?.startsWith('searchInput')) return;
      // fade the current example out, then show the next one (which fades in)
      setLeaving(true);
      swap = setTimeout(() => {
        // the visitor may have clicked into the box during the fade: keep the example they are looking at
        if (document.activeElement?.id?.startsWith('searchInput')) return setLeaving(false);
        setStep(nextHintStep);
        setLeaving(false);
      }, HINT_LEAVE_MS);
    }, HINT_ROTATE_MS);
    return () => {
      clearInterval(id);
      clearTimeout(swap);
      setLeaving(false);
    };
  }, [scope, arm, paused]);

  if (scope === 'other') return { placeholder: HINT_CONTROL, arm: null, leaving: false, animated: false };
  if (scope === 'keep') return { placeholder: undefined, arm: null, leaving: false, animated: false };
  // The first example is the native placeholder, so nothing blinks on first paint; the animated overlay takes over
  // from the first fade-out (same text, same place) and stays.
  const animated = arm === 'rotating' && (leaving || step > 0);
  return { placeholder: hintPlaceholder({ arm, step }), arm, leaving, animated };
};
